import { createElement, Fragment, type ReactNode } from 'react'

import type { MessageVars, PluralForms } from './i18n.types'

/** Fills `{name}` placeholders; unknown placeholders stay visible. */
export function fmt(template: string, vars: MessageVars = {}): string {
  return template.replace(/\{(\w+)\}/g, (all, key: string) =>
    key in vars ? String(vars[key]) : all,
  )
}

/** Picks the plural form for `count` in `locale` and fills `{count}`. */
export function plural(
  locale: string,
  forms: PluralForms,
  count: number,
  vars: MessageVars = {},
): string {
  const rule = new Intl.PluralRules(locale).select(count)

  return fmt(rule === 'one' ? forms.one : forms.other, { count, ...vars })
}

/**
 * A language's own name, e.g. `de` → "Deutsch" (falls back to the code).
 * `capitalize: false` keeps the language's own casing for use mid-sentence
 * ("magyar", "français").
 */
export function languageName(
  code: string,
  inLocale = code,
  capitalize = true,
): string {
  try {
    const name = new Intl.DisplayNames([inLocale], { type: 'language' }).of(
      code,
    )

    if (!capitalize) return name ?? code

    return name
      ? name.charAt(0).toLocaleUpperCase(inLocale) + name.slice(1)
      : code
  } catch {
    return code
  }
}

/**
 * Like `fmt`, but placeholders may be React nodes (links, emphasis), so
 * translations control the word order around them.
 */
export function interpolate(
  template: string,
  parts: Record<string, ReactNode>,
): ReactNode {
  return template
    .split(/(\{\w+\})/g)
    .filter(Boolean)
    .map((piece, i) => {
      const key = /^\{(\w+)\}$/.exec(piece)?.[1]

      return createElement(
        Fragment,
        { key: i },
        key && key in parts ? parts[key] : piece,
      )
    })
}
